import { useCallback, useEffect, useState } from "react";
import { FiCopy, FiList, FiX } from "react-icons/fi";

import { Modal } from "~/components/Modal";
import type { PaymentPlan } from "~/modules/sales/types";
import { insertToast } from "~/modules/toasts/services";
import { api } from "~/utils/api";
import { getLocalDate } from "~/utils/getLocalDate";

type Props = {
	plan: PaymentPlan;
};

type CourtesyInvite = {
	id: string;
	token: string;
	maxUsages: number | null;
	currentUsages: number;
	expiresAt: string | null;
	createdAt: string;
};

export const GeneratedLinksModal: React.FC<Props> = ({ plan }) => {
	const [showModal, setShowModal] = useState<boolean>(false);
	const [invites, setInvites] = useState<CourtesyInvite[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const handleOpenModal = useCallback(() => {
		setShowModal(true);
		fetchInvites();
	}, []);

	const handleCloseModal = useCallback(() => {
		setShowModal(false);
	}, []);

	const fetchInvites = useCallback(async () => {
		setLoading(true);
		try {
			const response = await api.get<CourtesyInvite[]>(
				`/admin/sales/plans/${plan.id}/invites`,
			);
			setInvites(response.data);
		} catch (e) {
			insertToast({
				title: "Erro",
				message: "Não foi possível carregar os links gerados",
				type: "error",
				duration: 3,
			});
		} finally {
			setLoading(false);
		}
	}, [plan.id]);

	const copyToClipboard = useCallback((token: string) => {
		const inviteUrl = `${window.location.origin}/checkout-cortesia/${token}`;
		navigator.clipboard.writeText(inviteUrl).then(() => {
			insertToast({
				title: "Sucesso",
				message: "Link copiado para a área de transferência!",
				type: "success",
				duration: 3,
			});
		});
	}, []);

	return (
		<>
			<button
				type="button"
				onClick={handleOpenModal}
				className="py-2 px-4 rounded-md bg-gpa-blue-100 hover:bg-gpa-blue-500 hover:text-white transition-colors text-gpa-blue-500 font-semibold flex items-center justify-center gap-2"
				title="Ver links gerados"
			>
				<FiList size={18} />
			</button>

			<Modal visible={showModal}>
				<div className="flex flex-col gap-4">
					<div className="flex items-center justify-between mb-4">
						<h1 className="text-xl">Links gerados: {plan.name}</h1>
						<button
							type="button"
							className="p-2 rounded-full hover:bg-slate-50"
							onClick={handleCloseModal}
						>
							<FiX size={26} />
						</button>
					</div>

					<div className="max-w-full overflow-hidden overflow-x-auto border border-gpa-blue-50 rounded-lg">
						<table className="w-full min-w-[500px] bg-white">
							<thead className="bg-gpa-blue-50">
								<tr className="text-left">
									<th className="p-3">Data de criação</th>
									<th className="p-3">Usos</th>
									<th className="p-3">Expiração</th>
									<th className="p-3">Ações</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan={4} className="p-4 text-center">
											Carregando...
										</td>
									</tr>
								) : invites.length === 0 ? (
									<tr>
										<td colSpan={4} className="p-4 text-center">
											Nenhum link gerado para este plano.
										</td>
									</tr>
								) : (
									invites.map((invite) => (
										<tr key={invite.id} className="even:bg-slate-50">
											<td className="p-3">{getLocalDate(invite.createdAt)}</td>
											<td className="p-3">
												{invite.currentUsages} /{" "}
												{invite.maxUsages ? invite.maxUsages : "∞"}
											</td>
											<td className="p-3">
												{invite.expiresAt
													? getLocalDate(invite.expiresAt)
													: "Sem limite"}
											</td>
											<td className="p-3">
												<button
													type="button"
													onClick={() => copyToClipboard(invite.token)}
													className="p-2 bg-gpa-blue-100 text-gpa-blue-500 rounded hover:bg-gpa-blue-500 hover:text-white transition-colors"
													title="Copiar Link"
												>
													<FiCopy size={16} />
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			</Modal>
		</>
	);
};
